import { SxProps } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useEffect, useMemo, useState } from 'react';
import { SearchInput } from './search-input';

interface DebouncedSearchInputProps {
  /**
   * Called with the search term once typing has paused for `delay` ms. Must be
   * referentially stable (a state setter, or wrapped in `useCallback`) —
   * a new function on every parent render restarts the debounce timer.
   */
  onDebouncedChange: (searchTerm: string) => void;
  delay?: number;
  fullWidth?: boolean;
  label?: string;
  placeholder?: string;
  showLabel?: boolean;
  sx?: SxProps;
}

/**
 * Search input that holds the typed value in local state and only reports it
 * to the parent on a debounce. Keystrokes re-render this component alone, so a
 * parent that renders an expensive result list stays out of the typing path.
 */
export const DebouncedSearchInput = ({
  onDebouncedChange,
  delay = 300,
  ...searchInputProps
}: DebouncedSearchInputProps) => {
  const [value, setValue] = useState('');

  const debouncedOnChange = useMemo(
    () => debounce(onDebouncedChange, delay),
    [onDebouncedChange, delay],
  );

  useEffect(() => () => debouncedOnChange.clear(), [debouncedOnChange]);

  return (
    <SearchInput
      {...searchInputProps}
      value={value}
      onChange={(searchTerm) => {
        setValue(searchTerm);
        debouncedOnChange(searchTerm);
      }}
    />
  );
};
